import {Injectable} from '@angular/core';
import {BehaviorSubject, interval, Observable, Subscription} from 'rxjs';
import {DefaultStudyInfo, User, UsersService, WorkflowInfo, WorkflowItemInfo} from '@core/interfaces/common/users';
import {filter, finalize, map, shareReplay, switchMap, takeUntil} from 'rxjs/operators';
import {APIResponse} from '@core/interfaces/system/system-common';
import {
    ActivationPollingState,
    EventStatusResponse,
    WorkflowActivateDto,
    WorkflowActivateStatusType,
    WorkflowService,
    WorkflowType,
} from '@core/interfaces/engin/workflow';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {UsersStore} from '@store/common/users.store';
import {isEqual} from 'lodash';
import {NbToastrService} from '@nebular/theme';
import {NbToastRef} from '@nebular/theme/components/toastr/toastr.service';

@Injectable()
export class StudiesStore extends Unsubscribable {
    private defaultStudyInfo: BehaviorSubject<DefaultStudyInfo> = new BehaviorSubject<DefaultStudyInfo>(null);
    readonly defaultStudyInfo$: Observable<DefaultStudyInfo> = this.defaultStudyInfo
        .asObservable()
        .pipe(shareReplay(1));

    private activeWorkflows: BehaviorSubject<WorkflowInfo[]> = new BehaviorSubject<WorkflowInfo[]>([]);
    readonly activeWorkflows$: Observable<WorkflowInfo[]> = this.activeWorkflows.asObservable().pipe(shareReplay(1));

    constructor(
        private userService: UsersService,
        private workflowService: WorkflowService,
        private userStore: UsersStore,
        private toastrService: NbToastrService,
    ) {
        super();

        this.userStore.currentUser$.subscribe((user: User) => {
            // Refresh related info on user change
            if (!!user) {
                this.userService.getDefaultStudyInfo().subscribe((resp: APIResponse<DefaultStudyInfo>) => {
                    if (resp?.response) {
                        this.setDefaultStudyInfo(resp.response);
                        this.setActiveWorkflows(resp.response.defaultWorkflowList);
                    }
                });
            }
        });
    }

    // Helper methods for default study info
    /**
     * Force fetch default study info. This will trigger an update via defaultStudyInfo$ observable.
     * @return void
     */
    public refreshDefaultStudyInfo(): void {
        this.userService.getDefaultStudyInfo().subscribe((resp: APIResponse<DefaultStudyInfo>) => {
            this.setDefaultStudyInfo(resp.response as DefaultStudyInfo);
        });
    }
    /**
     * Get default study info.
     * @return DefaultStudyInfo
     */
    public getDefaultStudyInfo(): DefaultStudyInfo {
        if (this.defaultStudyInfo.getValue() == null) this.refreshDefaultStudyInfo();
        return this.defaultStudyInfo.getValue();
    }
    /**
     * (Re)set the default study info based on provided input.
     * @param defaultStudyInfo
     * @private
     */
    public setDefaultStudyInfo(defaultStudyInfo: DefaultStudyInfo): void {
        this.defaultStudyInfo.next(defaultStudyInfo);
    }

    // Centralized logic for frequent access to active study and active study ID, by type
    readonly activeWorkflowRisk$: Observable<WorkflowInfo> = this.activeWorkflows$.pipe(
        map(
            (activeWorkflows: WorkflowInfo[]) =>
                activeWorkflows?.find((w) => w.workflowType === WorkflowType.STUDY) || null,
        ),
        shareReplay(1),
    );
    readonly activeStudyRisk$: Observable<WorkflowItemInfo> = this.activeWorkflowRisk$.pipe(
        map(
            (activeWorkflowRisk: WorkflowInfo) =>
                activeWorkflowRisk?.workflowItemList.find((i) => i.defaultItem) || null,
        ),
        shareReplay(1),
    );
    readonly activeStudyIdRisk$: Observable<number> = this.activeStudyRisk$.pipe(
        map((i: WorkflowItemInfo) => i?.workflowItemId),
        shareReplay(1),
        filter((d) => !!d && d != -1),
    );
    readonly activeWorkflowLf$: Observable<WorkflowInfo> = this.activeWorkflows$.pipe(
        map(
            (activeWorkflows: WorkflowInfo[]) =>
                activeWorkflows?.find((w) => w.workflowType === WorkflowType.LOAD_FORECAST) || null,
        ),
        shareReplay(1),
    );
    readonly activeStudyLf$: Observable<WorkflowItemInfo> = this.activeWorkflowLf$.pipe(
        map((activeWorkflowLf: WorkflowInfo) => activeWorkflowLf?.workflowItemList.find((i) => i.defaultItem)) || null,
        shareReplay(1),
    );
    readonly activeStudyIdLf$: Observable<number> = this.activeStudyLf$.pipe(
        map((i: WorkflowItemInfo) => i?.workflowItemId),
        shareReplay(1),
        filter((d) => !!d && d != -1),
    );

    // Helper methods for active study info
    /**
     * Force refresh active (study-typed) workflows. This will reset active (study-typed) workflows back to the
     * current default studies. This will trigger an update via activeWorkflows$ observable and related observables.
     * @return void
     */
    public refreshActiveWorkflows(): void {
        const defaultStudyInfo: DefaultStudyInfo = this.defaultStudyInfo.getValue();
        if (defaultStudyInfo) {
            this.setActiveWorkflows(defaultStudyInfo.defaultWorkflowList);
        }
    }
    /**
     * Get active studies.
     * @return WorkflowInfo[]
     */
    public getActiveWorkflows(): WorkflowInfo[] {
        return this.activeWorkflows.getValue();
    }

    /**
     * Update active (study-typed) workflow for provided workflow type. This will de-activate the existing workflow for
     * that type and activate the new workflow. This will trigger an update via activeStudies$ observable and related
     * observables.
     * @param type workflowType for (study-typed) workflow
     * @param workflowId workflowId for new (study-typed) workflow
     */
    public updateActiveWorkflowByType(type: WorkflowType, workflowId: number): void {
        const activeStudies: WorkflowInfo[] = this.activeWorkflows.getValue();
        const currentActiveWorkflowId: number = activeStudies.find((w) => w.workflowType === type)?.workflowId || -1;

        let foundAndReset: boolean = false;
        const newActiveStudies: WorkflowInfo[] = activeStudies.map((w: WorkflowInfo) => {
            if (w.workflowType === type) foundAndReset = true;
            return {
                ...w,
                workflowId: w.workflowId === currentActiveWorkflowId ? workflowId : w.workflowId,
            };
        });
        // If activeStudies is currently empty and first study is being activated, need to push manually
        if (!foundAndReset) {
            newActiveStudies.push({
                workflowId: workflowId,
                workflowType: type,
                workflowItemList: [], // this.setActiveStudies will call API and find the necessary data
            });
        }

        this.setActiveWorkflows(newActiveStudies);
    }
    /**
     * Get active (study-typed) workflow for a single workflow type. If none is found, return WorkflowInfo
     * will have id = -1.
     * @param type WorkflowType
     * @return WorkflowInfo active (study-typed) workflow
     */
    public getActiveWorkflowByType(type: WorkflowType): WorkflowInfo {
        if (!this.activeWorkflows.getValue() || this.activeWorkflows.getValue().length === 0)
            this.refreshActiveWorkflows();
        return this.activeWorkflows.getValue().find((w) => w.workflowType === type);
    }
    /**
     * Get active (study-typed) workflow ID for a single workflow type. If none is found, return -1.
     * @param type WorkflowType
     * @return number, active (study-typed) workflow
     */
    public getActiveWorkflowIdByType(type: WorkflowType): number {
        const workFlow = this.getActiveWorkflowByType(type);
        if (workFlow === undefined) {
            return -1;
        }
        return workFlow.workflowId;
    }
    /**
     * Get active "study" i.e., study-typed workflowItem for a single workflow type. If none is found,
     * return WorkflowItemInfo will have id = -1.
     * @param type WorkflowType
     * @return WorkflowItemInfo active (study-typed) workflow
     */
    public getActiveStudyByType(type: WorkflowType): WorkflowItemInfo {
        if (!this.activeWorkflows.getValue() || this.activeWorkflows.getValue().length === 0)
            this.refreshActiveWorkflows();
        return this.activeWorkflows
            .getValue()
            ?.find((w: WorkflowInfo) => w.workflowType === type)
            ?.workflowItemList.find((i: WorkflowItemInfo) => i.defaultItem);
    }
    /**
     * Get active "studyId" i.e., study-typed workflowItemId for a single workflow type. If none is found, return -1.
     * @param type WorkflowType
     * @return number, active (study-typed) workflow item
     */
    public getActiveStudyIdByType(type: WorkflowType): number {
        const workflowItem: WorkflowItemInfo = this.getActiveStudyByType(type);
        if (workflowItem === undefined) {
            return -1;
        }
        return workflowItem.workflowItemId;
    }
    /**
     * (Re)set the active (study-typed) workflow info based on provided input.
     * @param activeWorkflows
     * @private
     */
    private setActiveWorkflows(activeWorkflows: WorkflowInfo[]): void {
        if (activeWorkflows && !isEqual(activeWorkflows, this.activeWorkflows.getValue())) {
            this.setStudiesLoading(true);
            this.setStudyPending(true);

            const userId = this.userStore.getCurrentUser()?.id;

            if (!userId) return;

            // Start async activation with polling
            this.workflowService
                .activateWorkflowsByUserAsync(
                    userId,
                    activeWorkflows.map((w) => w.workflowId),
                )
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe({
                    next: (resp: APIResponse<WorkflowActivateDto>) => {
                        if (resp?.response) {
                            const activateResp: WorkflowActivateDto = resp.response;

                            if (activateResp.workflowActivateStatus == WorkflowActivateStatusType.LOADING) {
                                const eventId = resp.response.activateEventId;
                                this.startPollingForActivation(eventId, activeWorkflows);
                            } else {
                                // Study activation has completed, handle different statuses
                                switch (activateResp.workflowActivateStatus) {
                                    case WorkflowActivateStatusType.WORKFLOW_DELETED:
                                        this.handleActivationWarning(activateResp.message);
                                        break;
                                    case WorkflowActivateStatusType.WORKFLOW_ACTIVE:
                                        this.handleActivationSuccess(
                                            activateResp.activeWorkflows,
                                            activateResp.message,
                                        );
                                        break;
                                }
                            }
                        }
                    },
                    error: (error) => {
                        this.handleActivationFailure();
                    },
                });
        }
    }

    /**
     * Clear default study info and active studies e.g., during logout.
     */
    public clearStudyInfo(): void {
        this.setRequestsPending(true);

        const userId = this.userStore.getCurrentUser()?.id;
        userId &&
            this.workflowService
                .clearActiveWorkflowsByUser(userId)
                .pipe(
                    takeUntil(this.unsubscribe$),
                    finalize(() => this.setRequestsPending(false)),
                )
                .subscribe(() => {
                    this.activeWorkflows.next([]);
                    this.defaultStudyInfo.next(null);
                });
    }

    /**
     * Start polling for activation event status.
     * Polls every 10 seconds with max 30 attempts (5 minutes timeout).
     */
    private startPollingForActivation(eventId: number, activeWorkflows: WorkflowInfo[]): void {
        // Set initial polling state
        this.activationPolling.next({
            eventId,
            isPolling: true,
        });

        let attemptCount = 0;

        // Clean up any existing polling
        this.stopPolling();

        // Start polling with RxJS interval + switchMap pattern
        this.activeToastr = this.toastrService.info(
            'Study activation processing: this may take a few minutes.',
            'Study Activation Processing',
            {duration: 0},
        );

        this.pollingSubscription = interval(this.POLL_INTERVAL_MS)
            .pipe(
                takeUntil(this.unsubscribe$),
                switchMap(() => {
                    attemptCount++;
                    return this.workflowService.checkEventStatus(eventId);
                }),
                takeUntil(this.unsubscribe$),
            )
            .subscribe({
                next: (resp: APIResponse<EventStatusResponse>) => {
                    if (!resp?.response) return;

                    const status: EventStatusResponse = resp.response;

                    // Update polling state
                    this.activationPolling.next({
                        eventId,
                        isPolling: true,
                        lastStatus: status,
                    });

                    // Handle terminal states
                    if (status.status === 'success') {
                        this.handleActivationSuccess(status.activeWorkflows);
                    } else if (status.status === 'failed') {
                        this.handleActivationFailure(status.message);
                    } else if (attemptCount >= this.MAX_POLL_ATTEMPTS) {
                        this.handleActivationTimeout();
                    }
                },
                error: (error) => {
                    attemptCount++;

                    // Fail on max attempts
                    if (attemptCount >= this.MAX_POLL_ATTEMPTS) {
                        this.handleActivationFailure('Status check failed after multiple attempts');
                    }
                },
            });
    }

    /**
     * Stop active polling and clean up.
     */
    private stopPolling(): void {
        if (this.pollingSubscription) {
            this.pollingSubscription.unsubscribe();
            this.pollingSubscription = null;
        }

        this.activationPolling.next(null);
    }

    /**
     * End the activation process; stop polling new events, reset loading flags.
     */
    private endActivationProcess(): void {
        this.stopPolling();
        this.setStudiesLoading(false);
        this.setStudyPending(false);
    }

    /**
     * Handle successful activation completion.
     */
    private handleActivationSuccess(activeWorkflows: WorkflowInfo[], message: string = null): void {
        this.endActivationProcess();
        this.activeWorkflows.next(activeWorkflows);

        this.replaceToastr(
            this.toastrService.success(
                message || 'Study activation completed successfully.',
                'Study Activation Complete',
                {
                    duration: 10000,
                },
            ),
        );
    }

    /**
     * Handle activation ended with warning.
     */
    private handleActivationWarning(message: string): void {
        this.endActivationProcess();

        this.replaceToastr(
            this.toastrService.warning(
                message || 'Study activation cannot proceed: check your settings in Study History.',
                'Study Activation Cannot Proceed',
                {
                    duration: 10000,
                },
            ),
        );
    }

    /**
     * Handle activation ended with failure.
     */
    private handleActivationFailure(errorMessage: string = null): void {
        this.endActivationProcess();

        this.replaceToastr(
            this.toastrService.danger(
                errorMessage || 'Study activation failed. Please try again.',
                'Study Activation Failed',
                {
                    duration: 10000,
                },
            ),
        );
    }

    /**
     * Handle polling timeout.
     */
    private handleActivationTimeout(): void {
        this.handleActivationFailure(
            'Activation is taking longer than expected. Please refresh the page to check status.',
        );
    }

    // TODO: why do we have 3 flags for tracking studies loading?
    private studiesPending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly studiesPending$: Observable<boolean> = this.studiesPending.asObservable();

    private studyPending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly studyPending$: Observable<boolean> = this.studyPending.asObservable();

    private dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly dataLoading$: Observable<boolean> = this.dataLoading.asObservable();

    private requestsPending: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly requestsPending$: Observable<boolean> = this.requestsPending.asObservable().pipe(shareReplay(1));

    private lastLoadStatus: boolean = true;
    private activeToastr: NbToastRef;

    private activationPolling: BehaviorSubject<ActivationPollingState | null> =
        new BehaviorSubject<ActivationPollingState | null>(null);
    readonly activationPolling$: Observable<ActivationPollingState | null> = this.activationPolling
        .asObservable()
        .pipe(shareReplay(1));

    private pollingSubscription: Subscription | null = null;

    private readonly POLL_INTERVAL_MS = 10000; // 10 seconds
    private readonly MAX_POLL_ATTEMPTS = 60; // 10 minutes total

    setStudiesLoading(value: boolean) {
        this.studiesPending.next(value);
    }
    setStudyPending(value: boolean) {
        this.studyPending.next(value);
    }
    setDataLoading(value: boolean) {
        this.dataLoading.next(value);
    }

    setRequestsPending(value: boolean) {
        this.requestsPending.next(value);
    }

    ngOnDestroy(): void {
        this.stopPolling();
        super.ngOnDestroy();
    }

    private replaceToastr(newToastr: NbToastRef) {
        if (this.activeToastr) this.activeToastr.close();
        this.activeToastr = newToastr;
    }
}
